import { useContext, useState } from "react";
import { useEffect } from "react";

import SimpleModal from "../../../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../../../shared/definitions/datatypes";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import { getAllData, getData } from "../../../../../shared/utils/api";
import { IObject, validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ColumnSelector from "../../../column-selector/column-selector";
import SearchInput from "../../../search-input/search-input";
import { IWidgetModalProps } from "../../widget-utils";
import "./datamodel-related-entity-modal.scss";
import { IRelatedEntityWidget } from "../../widgets/related-entity-widget/related-entity-widget";
import { IColumn, ITableRowData } from "../../../data-table/data-table";

export interface IDatamodelRelatedEntityModal {
  type: "datamodelRelatedEntity";
}

const DatamodelRelatedEntityModal = ({modalOpen, widgetData, onWidgetChanged}: IWidgetModalProps<IRelatedEntityWidget>) => {
  const emptyWidgetData: IRelatedEntityWidget = {
    id: "",
    modalType: "datamodelRelatedEntity",
    type: "relatedEntity",
    label: "",
    modalData: {
      datamodelType: "entity"
    },
    datamodelId: "",
    selectedColumns: []
  };
  
  const authCtx = useContext(AuthContext);

  const [entities, setEntities] = useState<IObject[]>([]);
  const [showModal, setShowModal] = useState(modalOpen || false);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<IColumn[]>(widgetData?.selectedColumns || []);
  const [modalData, setModalData] = useState<ITableRowData>(widgetData?.id ? widgetData : {...emptyWidgetData});
  
  const { sendRequest: getEntities, status: getEntitiesStatus, data: getEntitiesData, error: getEntitiesError } = useHttp(getAllData, false, authCtx.token);
  const { sendRequest: getDatamodelDetails, data: getDatamodelData, status: getDatamodelStatus, error: getDatamodelError } = useHttpGet(getData, false, authCtx.token, undefined, "platform");

  useEffect(() => {
    setShowModal(modalOpen || false);
  }, [modalOpen]);

  useEffect(() => {
    if (widgetData?.id) {
      setModalData(widgetData?.id ? widgetData : {...emptyWidgetData});
      setSelectedColumns(widgetData?.selectedColumns || []);
      if (widgetData?.datamodelId) {
        getDatamodelDetails(`datamodel/entity/${widgetData?.datamodelId}`);
      }
    }
  }, [widgetData]);

  useEffect(() => {
    getEntities("datamodel/entities");
  }, []);

  useEffect(() => {
    if (getEntitiesData && !getEntitiesError && getEntitiesStatus === "completed") {
      setEntities(getEntitiesData.rows);
    }
  }, [getEntitiesStatus, getEntitiesData, getEntitiesError]);

  useEffect(() => {
    if (getDatamodelStatus === "completed" && !getDatamodelError && getDatamodelData) {
      const columns = getDatamodelData.fields.map((f: IObject) => ({
        key: f.input_field_name,
        displayName: f.field_name,
        dataType: {type:f.field_datatype || "text"}
      }) as IColumn);
  
      setColumns(columns);
    }
  }, [getDatamodelData, getDatamodelStatus, getDatamodelError]);

  const handleOnEntityChange = (selectedOption: IEnumerationOption | undefined) => {
    if (!selectedOption) {
      setColumns([]);
      setSelectedColumns([]);
      setModalData({...emptyWidgetData});
      return;
    }
    
    setModalData({...modalData, id: selectedOption.id, datamodelId: selectedOption.id, label: selectedOption.name});
    getDatamodelDetails(`datamodel/entity/${selectedOption.id}`);
  };

  const handleColumnSelectionChanged = (selectedColumns: IColumn[]) => {
    setSelectedColumns(selectedColumns);
  };

  const renderModalBody = () => {
    return <div>
      {validateLoadingStatusNew([getEntitiesStatus]) && <Spinner />}
      <SearchInput
        label="Entity"
        handleOnChange={handleOnEntityChange}
        data={entities.map((entity : IObject) => ({id: entity.id, name: entity.name}))}
        inputKey="entityId"
        required={true}
        selectedOption={modalData?.datamodelId || ""}
      />
      <div className="datamodelRelatedEntityModal-columnSelector">
        <ColumnSelector availableColumns={columns} selectedColumns={selectedColumns} onSelectionChanged={handleColumnSelectionChanged} />
      </div>
    </div>;
  };
  
  const handleModalSubmit = () => {
    const mappedSelectedColumns: IColumn[] = selectedColumns.map(column => {
      const matchedColumn = columns.find(col  => column.key === col.key);
      return {
        key: column.key,
        displayName: column.displayName,
        dataType: {type: matchedColumn?.dataType?.type || "text"}
      } as IColumn;
    });

    onWidgetChanged({...modalData, selectedColumns: mappedSelectedColumns} as IRelatedEntityWidget);
    setShowModal(false);
  };

  const handleModalClosed = () => {
    onWidgetChanged(undefined);
    setShowModal(false);
    setModalData({...emptyWidgetData});
    setSelectedColumns([]);
    setColumns([]);
  };

  return <div className="datamodelRelatedEntityModal">
    <span className="datamodelRelatedEntityModal-layoutButton" onClick={() => setShowModal(true)}>
      {"Related entity +"}
    </span>
    {showModal && <SimpleModal
      actionButtonDisabled={selectedColumns.length === 0}
      mode="Related entity"
      onModalClickHandler={noop}
      modalBody={renderModalBody()}
      buttonText={"Add"}
      handleModalClosed={handleModalClosed}
      handleSubmit={handleModalSubmit}
      show={showModal}
      allowScrollOnBody={false} 
    />}
  </div>;
};

export default DatamodelRelatedEntityModal;